import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
// Minimal coordinate presence
const CoordinateGrid = () => <div className="absolute inset-0 w-full h-full opacity-[0.015] pointer-events-none" style={{
  backgroundImage: 'linear-gradient(#F5F5F5 0.5px, transparent 0.5px), linear-gradient(90deg, #F5F5F5 0.5px, transparent 0.5px)',
  backgroundSize: '80px 80px'
}} aria-hidden="true" data-id="element-3833" />;
export function WorkMirror() {
  return <>
      <Helmet data-id="element-3834">
        <title data-id="element-3835">
          Work Mirror — See Your Work Patterns Before They Repeat | Lazy Monk
          Labs
        </title>
        <meta name="description" content="If your work life keeps repeating, it's not bad luck. Same conflicts. Same ceilings. Different company, same story. Work Mirror shows you the pattern." data-id="element-3836" />
        <meta name="robots" content="index, follow" data-id="element-3837" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-3838">
        {/* Navigation */}
        <header data-id="element-3839">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-3840">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-3841">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-3842">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-3843" />
                <span className="text-base font-mono tracking-tight text-gray-400" data-id="element-3844">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-3845">
                <Link to="/work-mirror" className="text-sm text-[#F5F5F5] hover:text-gray-400 transition-colors font-medium" data-id="element-3846">
                  Work Mirror
                </Link>
                <Link to="/decoder" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-3847">
                  Decoder
                </Link>
                <Link to="/careers" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-3848">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-3849">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16" data-id="element-3850">
          {/* 1. HERO - Pain First */}
          <section className="relative py-32 md:py-40 px-6 overflow-hidden" data-id="element-3851">
            <CoordinateGrid data-id="element-3852" />
            <div className="relative z-10 max-w-4xl mx-auto space-y-12" data-id="element-3853">
              <div className="space-y-8" data-id="element-3854">
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif leading-[1.1] text-[#F5F5F5]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-3855">
                  If your work life keeps repeating, it's not bad luck.
                </h1>

                <div className="space-y-4 text-xl md:text-2xl text-gray-400 font-light leading-relaxed" data-id="element-3856">
                  <p data-id="element-3857">Same conflicts.</p>
                  <p data-id="element-3858">Same ceilings.</p>
                  <p data-id="element-3859">Same politics.</p>
                  <p data-id="element-3860">Same frustration.</p>
                </div>

                <p className="text-lg text-[#F5F5F5] pt-6 border-t border-[#2B2B2B]" data-id="element-3861">
                  Different company. Same story.
                </p>
              </div>

              <div className="pt-8 space-y-4" data-id="element-3862">
                <Link to="/work-mirror/begin" className="inline-block px-12 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-gray-200 transition-colors" data-id="element-3863">
                  Start Work Mirror
                </Link>
                <p className="text-xs text-gray-600" data-id="element-3864">
                  10-minute diagnostic · No signup required
                </p>
              </div>
            </div>
          </section>

          {/* 2. RECOGNITION - "This is you if..." */}
          <section className="py-32 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-3865">
            <div className="max-w-4xl mx-auto" data-id="element-3866">
              <h2 className="text-2xl font-medium text-[#F5F5F5] mb-12" data-id="element-3867">
                You might recognize yourself if...
              </h2>

              <div className="space-y-6 text-lg text-gray-400 leading-relaxed" data-id="element-3868">
                <p data-id="element-3869">
                  • You keep changing roles but the same problems follow you
                </p>
                <p data-id="element-3870">• You keep clashing with the same kinds of people</p>
                <p data-id="element-3871">• You keep getting stuck at the same level</p>
                <p data-id="element-3872">• You keep thinking "this time will be different"</p>
                <p data-id="element-3873">• You can see other people's patterns but not your own</p>
              </div>
            </div>
          </section>

          {/* 3. THE HIDDEN CAUSE */}
          <section className="py-32 px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-3874">
            <div className="max-w-4xl mx-auto" data-id="element-3875">
              <div className="space-y-12" data-id="element-3876">
                <h2 className="text-3xl md:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-3877">
                  It's not bad intent.
                  <br data-id="element-3878" />
                  It's not bad luck.
                  <br data-id="element-3879" />
                  It's invisible structure.
                </h2>

                <div className="space-y-8 text-lg text-gray-400 leading-relaxed border-l-2 border-[#2B2B2B] pl-8" data-id="element-3880">
                  <p data-id="element-3881">
                    You only see your intentions.
                    <br data-id="element-3882" />
                    You don't see the pattern underneath.
                  </p>
                  <p data-id="element-3883">
                    The pattern runs automatically.
                    <br data-id="element-3884" />
                    It shapes decisions, reactions, and conflicts without you
                    noticing.
                  </p>
                  <p className="text-[#F5F5F5]" data-id="element-3885">Until it repeats.</p>
                </div>
              </div>
            </div>
          </section>

          {/* 4. THE COST */}
          <section className="py-32 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-3886">
            <div className="max-w-4xl mx-auto" data-id="element-3887">
              <h2 className="text-2xl font-medium text-[#F5F5F5] mb-12" data-id="element-3888">
                What this is silently costing you
              </h2>

              <div className="space-y-6 text-lg text-gray-400 leading-relaxed" data-id="element-3889">
                <p data-id="element-3890">• Years of slow drift</p>
                <p data-id="element-3891">• Repeating the same mistakes with better titles</p>
                <p data-id="element-3892">• Burning out without understanding why</p>
                <p data-id="element-3893">• Confusing effort with progress</p>
                <p data-id="element-3894">• Watching others advance while you loop</p>
              </div>

              <p className="text-lg text-[#F5F5F5] mt-12 pt-8 border-t border-[#2B2B2B]" data-id="element-3895">
                The pattern doesn't fix itself.
                <br data-id="element-3896" />
                It compounds.
              </p>
            </div>
          </section>

          {/* 5. WORK MIRROR - The Solution */}
          <section className="py-32 px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-3897">
            <div className="max-w-4xl mx-auto" data-id="element-3898">
              <div className="space-y-12" data-id="element-3899">
                <div data-id="element-3900">
                  <p className="text-xs font-mono text-[#64748B] uppercase tracking-widest mb-6" data-id="element-3901">
                    The Solution
                  </p>
                  <h2 className="text-4xl md:text-5xl font-serif leading-tight text-[#F5F5F5] mb-6" style={{
                  fontFamily: 'Georgia, serif'
                }} data-id="element-3902">
                    Work Mirror
                  </h2>
                  <p className="text-xl text-gray-400 leading-relaxed" data-id="element-3903">
                    A living mirror for how you actually operate at work.
                  </p>
                </div>

                <div className="space-y-8 text-lg text-gray-400 leading-relaxed border-l-2 border-[#2B2B2B] pl-8" data-id="element-3904">
                  <p data-id="element-3905">
                    Work Mirror shows you the pattern behind your work life —
                    while it's happening.
                  </p>
                  <p data-id="element-3906">
                    Not what you should do.
                    <br data-id="element-3907" />
                    What you actually do.
                  </p>
                  <p className="text-[#F5F5F5]" data-id="element-3908">
                    The pattern becomes visible before it repeats.
                  </p>
                </div>

                <div className="pt-8" data-id="element-3909">
                  <Link to="/work-mirror/begin" className="inline-block px-12 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-gray-200 transition-colors" data-id="element-3910">
                    Start Work Mirror
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* 6. HOW IT WORKS */}
          <section className="py-32 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-3911">
            <div className="max-w-4xl mx-auto" data-id="element-3912">
              <h2 className="text-2xl font-medium text-[#F5F5F5] mb-16" data-id="element-3913">
                How it works
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-16" data-id="element-3914">
                <div className="space-y-6" data-id="element-3915">
                  <div className="text-5xl font-serif text-[#64748B]" data-id="element-3916">1</div>
                  <h3 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-3917">
                    10-minute diagnostic
                  </h3>
                  <p className="text-base text-gray-400 leading-relaxed" data-id="element-3918">
                    Reveals your baseline work pattern — how you make decisions,
                    handle conflict, and respond to pressure.
                  </p>
                </div>

                <div className="space-y-6" data-id="element-3919">
                  <div className="text-5xl font-serif text-[#64748B]" data-id="element-3920">2</div>
                  <h3 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-3921">
                    2-minute daily reflection
                  </h3>
                  <p className="text-base text-gray-400 leading-relaxed" data-id="element-3922">
                    Shows the pattern playing out in real work situations. You
                    see it happening, not after it's too late.
                  </p>
                </div>
              </div>

              <p className="text-base text-[#F5F5F5] mt-16 pt-8 border-t border-[#2B2B2B]" data-id="element-3923">
                Static insight → Living mirror
              </p>
            </div>
          </section>

          {/* 7. WHAT THIS IS / IS NOT */}
          <section className="py-32 px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-3924">
            <div className="max-w-4xl mx-auto" data-id="element-3925">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-16" data-id="element-3926">
                {/* This is not */}
                <div className="space-y-8" data-id="element-3927">
                  <h2 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-3928">
                    This is not
                  </h2>
                  <div className="space-y-3 text-gray-500" data-id="element-3929">
                    <p data-id="element-3930">• Coaching</p>
                    <p data-id="element-3931">• Therapy</p>
                    <p data-id="element-3932">• Advice</p>
                    <p data-id="element-3933">• Productivity</p>
                    <p data-id="element-3934">• Motivation</p>
                  </div>
                </div>

                {/* This is */}
                <div className="space-y-8" data-id="element-3935">
                  <h2 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-3936">
                    This is
                  </h2>
                  <p className="text-xl text-[#F5F5F5]" data-id="element-3937">A mirror.</p>
                  <p className="text-base text-gray-400 leading-relaxed" data-id="element-3938">
                    It shows you the structure underneath your work behavior.
                    <br data-id="element-3939" />
                    <br data-id="element-3940" />
                    What you do with that is up to you.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 8. THE ENGINE BEHIND IT */}
          <section className="py-32 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-3941">
            <div className="max-w-4xl mx-auto" data-id="element-3942">
              <div className="space-y-12" data-id="element-3943">
                <div data-id="element-3944">
                  <p className="text-xs font-mono text-[#64748B] uppercase tracking-widest mb-6" data-id="element-3945">
                    The Engine
                  </p>
                  <h2 className="text-2xl font-medium text-[#F5F5F5] mb-6" data-id="element-3946">
                    Built on Decoder
                  </h2>
                  <p className="text-lg text-gray-400 leading-relaxed" data-id="element-3947">
                    The pattern recognition engine by Lazy Monk Labs.
                  </p>
                </div>

                <div className="space-y-6 text-gray-400 leading-relaxed border-l-2 border-[#2B2B2B] pl-6" data-id="element-3948">
                  <p data-id="element-3949">Decoder maps:</p>
                  <div className="space-y-2 text-base" data-id="element-3950">
                    <p data-id="element-3951">• Decision loops</p>
                    <p data-id="element-3952">• Behavioral patterns</p>
                    <p data-id="element-3953">• Feedback cycles</p>
                    <p data-id="element-3954">• Structural constraints</p>
                  </div>
                  <p className="text-[#F5F5F5] pt-6" data-id="element-3955">
                    Work Mirror is Decoder in continuous form.
                  </p>
                </div>

                <div className="pt-8" data-id="element-3956">
                  <Link to="/decoder" className="inline-flex items-center gap-3 text-base text-[#F5F5F5] hover:text-gray-400 transition-colors group" data-id="element-3957">
                    <span data-id="element-3958">Learn about Decoder</span>
                    <span className="text-gray-600 group-hover:text-gray-400 transition-colors" data-id="element-3959">
                      →
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* 9. FINAL CTA - Strong */}
          <section className="py-32 px-6 relative overflow-hidden border-t border-[#2B2B2B]" data-id="element-3960">
            <CoordinateGrid data-id="element-3961" />
            <div className="relative z-10 max-w-4xl mx-auto space-y-12" data-id="element-3962">
              <div className="space-y-8" data-id="element-3963">
                <h2 className="text-3xl md:text-5xl font-serif text-[#F5F5F5] leading-tight" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-3964">
                  The pattern is already running.
                  <br data-id="element-3965" />
                  You just can't see it yet.
                </h2>

                <p className="text-lg text-gray-400 leading-relaxed" data-id="element-3966">
                  Start with the diagnostic.
                  <br data-id="element-3967" />
                  See what's been invisible.
                </p>
              </div>

              <div className="pt-8 space-y-4" data-id="element-3968">
                <Link to="/work-mirror/begin" className="inline-block px-12 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-gray-200 transition-colors" data-id="element-3969">
                  Start Work Mirror
                </Link>
                <p className="text-xs text-gray-600" data-id="element-3970">
                  10-minute diagnostic · No signup required · Free to start
                </p>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="py-12 px-6 border-t border-[#2B2B2B]" data-id="element-3971">
          <div className="max-w-6xl mx-auto" data-id="element-3972">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8" data-id="element-3973">
              <div data-id="element-3974">
                <p className="text-sm text-gray-500 font-mono" data-id="element-3975">
                  Lazy Monk Labs
                </p>
                <p className="text-xs text-gray-600 mt-1" data-id="element-3976">
                  Pattern recognition systems
                </p>
              </div>

              <div className="flex gap-8 text-sm text-gray-500" data-id="element-3977">
                <Link to="/work-mirror" className="hover:text-[#F5F5F5] transition-colors" data-id="element-3978">
                  Work Mirror
                </Link>
                <Link to="/decoder" className="hover:text-[#F5F5F5] transition-colors" data-id="element-3979">
                  Decoder
                </Link>
                <Link to="/careers" className="hover:text-[#F5F5F5] transition-colors" data-id="element-3980">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-3981">
                  Contact
                </a>
              </div>

              <div className="text-xs text-gray-600 font-mono" data-id="element-3982">
                © {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>;
}